import { useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { Icon, type LatLngExpression } from 'leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import type { PropertySummary } from '../api/types';
import { moneyCompact } from '../utils/format';

// The tile provider is configuration. OpenStreetMap is the default and needs no key; any
// other provider can be swapped in through the environment without touching this code.
const TILE_URL = import.meta.env.VITE_MAP_TILE_URL || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = import.meta.env.VITE_MAP_ATTRIBUTION || '&copy; OpenStreetMap contributors';

const DEFAULT_CENTRE: LatLngExpression = [
  Number(import.meta.env.VITE_DEFAULT_LAT ?? 13.0827),
  Number(import.meta.env.VITE_DEFAULT_LNG ?? 80.2707),
];

// Leaflet's default marker images are resolved relative to the CSS, which breaks under a
// bundler. An inline SVG avoids the broken-image problem entirely.
const markerIcon = new Icon({
  iconUrl:
    'data:image/svg+xml;base64,' +
    btoa(
      `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
         <path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26c0-7.7-6.3-14-14-14z" fill="#0f766e"/>
         <circle cx="14" cy="14" r="5.5" fill="#fff"/>
       </svg>`,
    ),
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -36],
});

/** Keeps the viewport in step with the results as filters change. */
function FitToMarkers({ points }: { points: [number, number][] }) {
  const map = useMap();

  useMemo(() => {
    if (points.length === 0) return;

    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }

    map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
  }, [map, points]);

  return null;
}

export function ResultsMap({ properties }: { properties: PropertySummary[] }) {
  const mappable = properties.filter(
    (p): p is PropertySummary & { latitude: number; longitude: number } =>
      p.latitude !== undefined && p.longitude !== undefined,
  );

  const points = mappable.map((p) => [p.latitude, p.longitude] as [number, number]);

  if (mappable.length === 0) {
    return (
      <div className="sf-card sf-card-pad text-center sf-muted">
        None of these listings has been placed on the map yet, so there is nothing to show here. Switch back to the
        list view to see them.
      </div>
    );
  }

  return (
    <div className="sf-map">
      <MapContainer center={points[0] ?? DEFAULT_CENTRE} zoom={12} style={{ height: '100%', width: '100%' }}>
        <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
        <FitToMarkers points={points} />

        {mappable.map((property) => (
          <Marker key={property.id} position={[property.latitude, property.longitude]} icon={markerIcon}>
            <Popup>
              <strong>{property.title}</strong>
              <br />
              <span className="sf-muted">{property.locality}</span>
              <br />
              {property.priceFrom !== undefined && (
                <>
                  {moneyCompact(property.priceFrom)}/{property.priceUnit}
                  <br />
                </>
              )}
              <Link to={`/listing/${property.slug}`}>View listing</Link>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {mappable.length < properties.length && (
        <p className="form-text px-2 py-1 mb-0">
          {properties.length - mappable.length} of these listings has no map location and is not shown here.
        </p>
      )}
    </div>
  );
}

/** Single-property map used on the detail page. */
export function PropertyMap({
  latitude,
  longitude,
  title,
}: {
  latitude?: number;
  longitude?: number;
  title: string;
}) {
  if (latitude === undefined || longitude === undefined) {
    return (
      <div className="sf-card sf-card-pad sf-muted">
        The owner has not placed this property on the map. Use the address above for directions.
      </div>
    );
  }

  return (
    <div className="sf-map sf-map--compact">
      <MapContainer center={[latitude, longitude]} zoom={15} style={{ height: '100%', width: '100%' }}>
        <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
        <Marker position={[latitude, longitude]} icon={markerIcon}>
          <Popup>{title}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}
